import { type ReactElement, useId } from "react";
import {
  Box,
  FormControl,
  Switch,
  type SwitchProps,
  Typography,
} from "@mui/material";

import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form";

type SwitchInputProps<TForm extends FieldValues> = {
  fieldName: Path<TForm>;
  control: Control<TForm>;
  label: string;
  hideLabel?: boolean;
  hide?: boolean;
  ariaLabel?: string;
} & SwitchProps;

export const SwitchInput = <TForm extends FieldValues>({
  fieldName,
  control,
  label,
  hideLabel = false,
  hide,
  ...inputProps
}: SwitchInputProps<TForm>): ReactElement | null => {
  const id = useId();

  const {
    fieldState: { error },
    field,
  } = useController({
    name: fieldName,
    control,
  });

  if (hide) {
    return null;
  }

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%" }}
    >
      {!hideLabel && <Typography id={id}>{label}</Typography>}
      <FormControl error={!!error} size={"small"}>
        <Switch
          {...field}
          {...inputProps}
          onChange={(...args) => {
            field.onChange(...args);
            inputProps.onChange?.(...args);
          }}
          slotProps={{
            input: {
              "aria-labelledby": hideLabel ? undefined : id,
              "aria-label": hideLabel ? label : undefined,
            },
          }}
          checked={!!field.value}
          value={!!field.value}
        />
      </FormControl>
    </Box>
  );
};
